import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import type { CitationGroup } from "./types";

/** Tooltip body: verbatim cited fragment(s) + file source · locator. */
export function citationTooltip(group: CitationGroup): React.ReactNode {
  return (
    <Box>
      {group.citations.map((citation, i) => (
        <Typography
          key={i}
          variant="caption"
          component="div"
          sx={{ display: "block", mb: 0.5 }}
        >
          “{citation.citedText}”
          <Box component="span" sx={{ display: "block", opacity: 0.75 }}>
            {citation.sourceLabel || citation.evidenceId} · {citation.locator.raw}
          </Box>
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Turn clean (token-stripped) text + citation groups into inline React nodes:
 * plain text interleaved with clickable, tooltip-backed citation anchors. The
 * returned nodes are inline (no block wrapper) so they compose inside prose,
 * paragraphs, and list items alike. Shared by `CitedNarrative` (narrative
 * review) and `CitedMarkdown` (plan Outline).
 */
export function renderCitedSegments(
  cleanText: string,
  groups: CitationGroup[],
  onCite: ((group: CitationGroup) => void) | undefined,
  keyBase: string,
): React.ReactNode[] {
  const sorted = [...groups].sort((a, b) => a.anchorStart - b.anchorStart);
  const nodes: React.ReactNode[] = [];
  let cursor = 0;

  sorted.forEach((group, i) => {
    const start = Math.max(group.anchorStart, cursor);
    const end = Math.max(group.anchorEnd, start);
    if (start > cursor) nodes.push(cleanText.slice(cursor, start));

    const anchorText = cleanText.slice(start, end);
    const activate = () => onCite?.(group);

    nodes.push(
      <Tooltip key={`${keyBase}-cite-${i}`} title={citationTooltip(group)} arrow>
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-label={`View source: ${group.citations[0]?.citedText ?? "citation"}`}
          data-citation-anchor="true"
          onClick={activate}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              activate();
            }
          }}
          sx={{
            cursor: "pointer",
            color: "primary.main",
            textDecoration: "underline",
            textDecorationStyle: "dotted",
            textUnderlineOffset: "2px",
            "&:hover": { textDecorationStyle: "solid" },
          }}
        >
          {anchorText || "↗"}
        </Box>
      </Tooltip>,
    );
    cursor = end;
  });

  if (cursor < cleanText.length) nodes.push(cleanText.slice(cursor));
  return nodes;
}
